import { useState } from 'react';
import { Shell } from '../../components/layout/Shell.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import {
  OPTIONS_ST,
  REMISE_ANNUELLE,
  economieAnnuelle,
  grilleDuSecteur,
  prixPeriode,
} from '../../constants/plans';
import { useReglages } from '../../api/reglages.jsx';
import { societeApi } from '../../api/resources';
import { ACCESS_ROLE } from '../../lib/access';
import { fmt } from '../../lib/currency';
import { CLE_PACK_IA, basculePackIa, moisFacture, packIaActif } from '../../lib/pack-ia';
import { C } from '../../theme/palette';
import { ChangementModule } from './ChangementModule.jsx';
import { IntegrationsCard } from './IntegrationsCard.jsx';

/** Taux normal de la TVA suisse. */
const TVA = 0.081;

/** Codes des suppléments de taille, dans l'ordre de la grille. */
const TAILLES = [
  ['', 'Aucune'],
  ['petite', OPTIONS_ST.entreprise[0].taille],
  ['moyenne', OPTIONS_ST.entreprise[1].taille],
  ['grande', OPTIONS_ST.entreprise[2].taille],
];

/**
 * Le bandeau du haut, selon l'état que le serveur a donné à la société.
 *
 * @param {object|null} abo abonnement porté par la société
 * @returns {{sur: string, titre: string, sous: string}} les trois lignes du bandeau
 */
export function banniereAbonnement(abo) {
  const etat = abo?.etat || 'ESSAI';
  if (etat === 'ACTIF' || etat === 'SUSPENDU') {
    const prix =
      abo.periodicite === 'ANNUEL' ? abo.prixAnnuel + '.–/an HT' : abo.prixMensuel + '.–/mois HT';
    return {
      sur: etat === 'ACTIF' ? 'Formule actuelle' : 'Abonnement suspendu',
      titre: abo.nomFormule || abo.formule || '',
      sous: etat === 'ACTIF' ? prix : 'Reprends-la en la choisissant à nouveau',
    };
  }
  if (etat === 'ESSAI_TERMINE') {
    return {
      sur: 'Essai terminé',
      titre: 'Choisis une formule',
      sous: "Tes devis, factures et chantiers sont conservés : confirme une formule pour y retrouver l'accès",
    };
  }
  const jours = abo?.joursEssaiRestants;
  return {
    sur: 'Essai gratuit',
    titre:
      jours === undefined || jours === null
        ? '30 jours offerts'
        : jours + ' jour' + (jours > 1 ? 's' : '') + ' restant' + (jours > 1 ? 's' : ''),
    sous: 'Sans carte bancaire · résiliable à tout moment',
  };
}

export function AbonnementPage({
  onNav,
  soc,
  estMixte,
  univers,
  setUnivers,
  onAbonnement,
  onModule,
  aConfirmer = false,
}) {
  const PLANS = grilleDuSecteur(soc?.secteur, estMixte);
  // L'etat de l'abonnement est celui du serveur : c'est lui qui sait quelle
  // formule est active et ce qu'elle coute. La grille locale sert a
  // l'affichage des paliers ; les prix qu'il a calcules priment.
  const abo = soc?.abonnement || null;
  const patron = ACCESS_ROLE === 'patron';
  const { reglageJson, setReglageJson } = useReglages();
  const etatIa = reglageJson(CLE_PACK_IA, null);
  const iaActif = packIaActif(etatIa);
  const [annuel, setAnnuel] = useState(abo?.periodicite === 'ANNUEL');
  const [taille, setTaille] = useState(abo?.options?.taille || '');
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState('');
  // Comme la maquette, les prix TTC s'affichent sous le prix HT ; celui qui
  // n'est pas assujetti à la TVA coupe la bascule.
  const [ttc, setTtc] = useState(true);
  const banniere = banniereAbonnement(abo);

  const prixDe = (p) => {
    const serveur = (abo?.grille || []).find((g) => g.code === p.id);
    if (serveur) return annuel ? serveur.prixAnnuel : serveur.prixMensuel;
    return prixPeriode(p, annuel);
  };

  const choisir = async (p) => {
    setErreur('');
    setEnCours(p.id);
    try {
      const nouveau = await societeApi.choisitAbonnement({
        formule: p.id,
        periodicite: annuel ? 'ANNUEL' : 'MENSUEL',
        options: { taille: taille || null, packIa: iaActif },
      });
      if (typeof onAbonnement === 'function') onAbonnement(nouveau);
    } catch (e) {
      setErreur(e?.message || "La formule n'a pas pu être enregistrée.");
    } finally {
      setEnCours(null);
    }
  };

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="⭐ Abonnement"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div
        style={{
          background: 'linear-gradient(135deg,' + C.navy + ',' + C.navyM + ')',
          borderRadius: 14,
          padding: 16,
          marginBottom: 14,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 9,
            color: 'rgba(255,255,255,.6)',
            textTransform: 'uppercase',
            letterSpacing: 1,
          }}
        >
          {banniere.sur}
        </div>
        <div style={{ fontSize: 24, fontWeight: 900, color: '#fff' }}>{banniere.titre}</div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)', marginTop: 4 }}>
          {banniere.sous}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 0,
          background: C.navyXL,
          borderRadius: 11,
          padding: 4,
          marginBottom: 14,
        }}
      >
        <div
          onClick={() => setAnnuel(false)}
          style={{
            flex: 1,
            textAlign: 'center',
            padding: '9px',
            borderRadius: 8,
            fontSize: 11.5,
            fontWeight: 800,
            cursor: 'pointer',
            background: !annuel ? '#fff' : 'transparent',
            color: !annuel ? C.ink : C.muted,
            boxShadow: !annuel ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
          }}
        >
          Mensuel
        </div>
        <div
          onClick={() => setAnnuel(true)}
          style={{
            flex: 1,
            textAlign: 'center',
            padding: '9px',
            borderRadius: 8,
            fontSize: 11.5,
            fontWeight: 800,
            cursor: 'pointer',
            background: annuel ? '#fff' : 'transparent',
            color: annuel ? C.ink : C.muted,
            boxShadow: annuel ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
          }}
        >
          Annuel{' '}
          <span
            style={{
              fontSize: 8,
              fontWeight: 800,
              color: '#fff',
              background: C.green,
              borderRadius: 5,
              padding: '1px 6px',
              marginLeft: 3,
            }}
          >
            −{Math.round(REMISE_ANNUELLE * 100)}%
          </span>
        </div>
      </div>
      <div
        onClick={() => setTtc(!ttc)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          marginBottom: 12,
          cursor: 'pointer',
          fontSize: 11,
          fontWeight: 600,
          color: C.grey,
        }}
      >
        <span
          style={{
            width: 36,
            height: 20,
            borderRadius: 99,
            background: ttc ? C.green : C.border,
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: ttc ? 18 : 2,
              width: 16,
              height: 16,
              borderRadius: 99,
              background: '#fff',
            }}
          />
        </span>
        <span>
          Afficher les prix <b>TTC</b> (TVA 8.1%) — seulement si tu es assujetti à la TVA
        </span>
      </div>
      {erreur && (
        <div
          style={{
            background: C.redL,
            color: C.red,
            borderRadius: 9,
            padding: '8px 12px',
            fontSize: 10,
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          ⚠ {erreur}
        </div>
      )}
      {/*
        Venu du voile de fin d'essai par « J'ai souscrit » : l'acces ne s'ouvre
        qu'une fois la formule souscrite confirmee ici et enregistree.
      */}
      {aConfirmer && patron && abo?.etat !== 'ACTIF' && (
        <div
          role="status"
          style={{
            background: C.navyXL,
            color: C.ink,
            borderRadius: 9,
            padding: '9px 12px',
            fontSize: 10,
            fontWeight: 700,
            marginBottom: 10,
            lineHeight: 1.5,
          }}
        >
          Confirme ci-dessous la formule que tu as souscrite : l&apos;accès à SwissTech s&apos;ouvre
          dès sa confirmation.
        </div>
      )}
      {!patron && (
        <div
          style={{
            fontSize: 8.5,
            color: C.muted,
            textAlign: 'center',
            marginBottom: 10,
            lineHeight: 1.5,
          }}
        >
          Seul le patron peut choisir ou changer la formule de la société.
        </div>
      )}
      {PLANS.map((p) => {
        // Pendant l'essai, la formule retenue a l'inscription n'est pas encore
        // souscrite : elle se signale, et se confirme d'un « Choisir ».
        const souscrite = abo?.etat === 'ACTIF' || abo?.etat === 'SUSPENDU';
        const retenue = abo?.formule === p.id;
        const on = retenue && souscrite;
        const prix = prixDe(p);
        return (
          <Card
            key={p.id}
            style={{ marginBottom: 11, border: '2px solid ' + (on ? p.col : C.border) }}
          >
            <div style={{ padding: '14px 15px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 10,
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      background: p.col + '18',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                    }}
                  >
                    {p.ic}
                  </span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>
                      {p.nom}
                      {on && (
                        <span style={{ fontSize: 8, color: p.col, marginLeft: 6 }}>● Actif</span>
                      )}
                      {retenue && !souscrite && (
                        <span style={{ fontSize: 8, color: p.col, marginLeft: 6 }}>
                          ● Retenue à l&apos;inscription
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 8, color: C.muted }}>
                      {annuel ? 'par an, HT' : 'par mois, HT'}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: p.col }}>{prix}.–</div>
                  {ttc && (
                    <div style={{ fontSize: 8.5, color: C.teal, fontWeight: 700, marginTop: 1 }}>
                      {/* Avec le séparateur des milliers, comme partout : « 1'038.84 ». */}
                      TTC {fmt(prix * (1 + TVA))}
                    </div>
                  )}
                  {annuel && (
                    <>
                      {/*
                        Le prix annuel ramené au mois : c'est celui-là que le
                        client compare à son abonnement actuel.
                      */}
                      <div style={{ fontSize: 8, color: C.muted }}>
                        soit {Math.round(prix / 12)}.–/mois
                      </div>
                      <div style={{ fontSize: 8, color: C.green, fontWeight: 700 }}>
                        économisez {economieAnnuelle(p)}.–/an
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: p.col, padding: '0 0 7px' }}>
                {p.users} utilisateurs (en même temps) · {p.emp} employés
              </div>
              {p.feats.map((ft, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 9.5,
                    color: C.grey,
                    padding: '3px 0',
                    display: 'flex',
                    gap: 7,
                  }}
                >
                  <span style={{ color: p.col }}>✓</span>
                  {ft}
                </div>
              ))}
              {patron && (
                <div style={{ marginTop: 10 }}>
                  <Btn
                    label={
                      on
                        ? 'Ta formule actuelle'
                        : enCours === p.id
                          ? 'Enregistrement…'
                          : retenue
                            ? 'Confirmer ' + p.nom
                            : 'Choisir ' + p.nom
                    }
                    color={p.col}
                    full
                    disabled={on || enCours !== null}
                    onClick={() => choisir(p)}
                  />
                </div>
              )}
            </div>
          </Card>
        );
      })}
      <Card style={{ marginBottom: 11, border: '2px dashed ' + C.purple }}>
        <div style={{ padding: '14px 15px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: C.purple + '18',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                }}
              >
                🤖
              </span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800 }}>
                  Pack IA{' '}
                  <span style={{ fontSize: 8, color: C.purple, fontWeight: 700 }}>
                    {iaActif ? '● ACTIF' : 'OPTION'}
                  </span>
                </div>
                <div style={{ fontSize: 8, color: C.muted }}>en plus de ta formule</div>
              </div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: C.purple }}>
              +{OPTIONS_ST.packIA.prix}.–
            </div>
          </div>
          {['Lecture automatique des factures (OCR)', 'Dictée vocale libre des devis'].map((ft) => (
            <div
              key={ft}
              style={{ fontSize: 9.5, color: C.grey, padding: '3px 0', display: 'flex', gap: 7 }}
            >
              <span style={{ color: C.purple }}>✓</span>
              {ft}
            </div>
          ))}
          <div
            style={{
              fontSize: 9.5,
              color: C.purple,
              padding: '3px 0',
              display: 'flex',
              gap: 7,
              fontWeight: 700,
            }}
          >
            <span>📊</span>Limite : {OPTIONS_ST.packIA.limite}
          </div>
          {/*
            Le pack s'ajoute et se retire en un geste, a tout moment. La
            facturation part du mois de l'activation, jamais du debut de
            l'abonnement : un client qui l'ajoute en septembre ne doit rien
            pour janvier.
          */}
          <div style={{ marginTop: 10 }}>
            <Btn
              label={iaActif ? '✓ Pack IA actif — désactiver' : 'Activer le Pack IA'}
              color={iaActif ? C.muted : C.purple}
              full
              onClick={() => setReglageJson(CLE_PACK_IA, basculePackIa(etatIa))}
            />
            {iaActif && moisFacture(etatIa) && (
              <div style={{ fontSize: 8.5, color: C.muted, textAlign: 'center', marginTop: 5 }}>
                Facturé depuis {moisFacture(etatIa)} · +{OPTIONS_ST.packIA.prix}.–/mois
              </div>
            )}
          </div>
        </div>
      </Card>
      <ChangementModule soc={soc} estMixte={estMixte} patron={patron} onModule={onModule} />
      <Card style={{ marginBottom: 11 }}>
        <div style={{ padding: '14px 15px' }}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 2 }}>
            Taille de l&apos;entreprise
          </div>
          <div style={{ fontSize: 8, color: C.muted, marginBottom: 6 }}>
            Option en plus de ta formule, enregistrée au prochain « Choisir »
          </div>
          {/*
            Une seule taille a la fois ; elle s'enregistre avec la formule au
            prochain « Choisir », et compte dans le prix que le serveur calcule.
          */}
          {TAILLES.map(([code, libelle], i) => {
            const option = code ? OPTIONS_ST.entreprise[i - 1] : null;
            const choisie = taille === code;
            return (
              <div
                key={code || 'aucune'}
                onClick={() => patron && setTaille(code)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 9.5,
                  color: choisie ? C.ink : C.grey,
                  fontWeight: choisie ? 800 : 500,
                  padding: '4px 0',
                  cursor: patron ? 'pointer' : 'default',
                }}
              >
                <span>
                  <span style={{ color: choisie ? C.green : C.border, marginRight: 6 }}>
                    {choisie ? '◉' : '○'}
                  </span>
                  {libelle}
                </span>
                <b style={{ color: C.ink }}>{option ? '+' + option.prix + '.–/mois' : '—'}</b>
              </div>
            );
          })}
        </div>
      </Card>
      {/* Les réglages d'envoi ne se lisent qu'en direction et en comptabilité :
          les demander pour la secrétaire valait une erreur 403 à chaque ouverture. */}
      <IntegrationsCard
        iaActif={iaActif}
        {...(patron || ACCESS_ROLE === 'comptable'
          ? {}
          : { chargeEnvois: () => Promise.resolve(null) })}
      />
      <div style={{ textAlign: 'center', fontSize: 8, color: C.muted, marginTop: 6 }}>
        Prix indicatifs en CHF, hors TVA sauf mention contraire. L'encaissement de l'abonnement
        reste à raccorder à un prestataire de paiement : choisir une formule l'enregistre, sans
        prélèvement.
      </div>
    </Shell>
  );
}
